import React from "react";
import { Line } from "react-chartjs-2";
import { fmt } from "../../services/formatting";

/**
 * Cumulative profit chart component
 */
const chartThemes = {
  dark: {
    text: "rgba(232,220,200,0.68)",
    soft: "rgba(232,220,200,0.48)",
    grid: "rgba(232,220,200,0.14)",
    accent: "#ce9210",
    panel: "#000000fa",
    border: "#da860715",
    tooltipBg: "rgba(0,0,0,0.8)",
    tooltipTitle: "#e8dcc8",
    positive: "#5de0a3",
    danger: "#ff6b5f",
    positiveFill: "rgba(93,224,163,0.12)",
    dangerFill: "rgba(255,107,95,0.12)",
  },
  light: {
    text: "rgba(28,24,18,0.82)",
    soft: "rgba(28,24,18,0.68)",
    grid: "rgba(28,24,18,0.22)",
    accent: "#6f470c",
    panel: "#f8faf9",
    border: "rgba(147,105,28,0.2)",
    tooltipBg: "rgba(248,250,249,0.96)",
    tooltipTitle: "#1c1812",
    positive: "#155f3c",
    danger: "#8f221b",
    positiveFill: "rgba(21,95,60,0.14)",
    dangerFill: "rgba(143,34,27,0.14)",
  },
};

const getChartPixelRatio = () => {
  const ratio = typeof window === "undefined" ? 1 : window.devicePixelRatio || 1;
  return Math.max(2, ratio * 1.5);
};

export const GraficoLucro = ({
  chartData,
  showWithoutSat,
  onToggleSat,
  theme,
  displayCurrency,
}) => {
  const colors = chartThemes[theme] || chartThemes.dark;
  const finalValue = chartData.datasets[0]?.data?.at(-1) || 0;
  const themedChartData = {
    ...chartData,
    datasets: chartData.datasets.map((dataset) => ({
      ...dataset,
      borderColor:
        dataset.data[dataset.data.length - 1] >= 0
          ? colors.positive || dataset.borderColor
          : colors.danger || dataset.borderColor,
      backgroundColor:
        dataset.data[dataset.data.length - 1] >= 0
          ? colors.positiveFill || dataset.backgroundColor
          : colors.dangerFill || dataset.backgroundColor,
      pointBackgroundColor: colors.accent,
      pointBorderColor: colors.panel,
    })),
  };

  if (chartData.labels.length === 0) {
    return (
      <div className="chart-empty-state">
        Sem dados para exibir.
        <br />
        <span className="chart-empty-state__hint">
          Registre participações para visualizar o gráfico.
        </span>
      </div>
    );
  }

  return (
    <div className={`motion-chart-card chart-card chart-card--${theme}`}>
      <div className="chart-card__header">
        <div className="chart-heading">
          <h2 className="chart-card__title">
            * Lucro Acumulado
          </h2>
          <div className="motion-chart-final">
            <span>Final</span>
            <strong>
              {finalValue >= 0 ? "+" : ""}
              {fmt(finalValue, displayCurrency, { minimumFractionDigits: 0 })}
            </strong>
          </div>
        </div>
        <label className="chart-sat-toggle">
          <input
            type="checkbox"
            checked={showWithoutSat}
            onChange={(e) => onToggleSat(e.target.checked)}
          />
          <span className="chart-sat-toggle__track" aria-hidden="true">
            <span className="chart-sat-toggle__thumb" />
          </span>
          Excluir Satélites
        </label>
      </div>

      <div className="motion-chart-frame">
        <Line
        key={theme}
        data={themedChartData}
        options={{
          devicePixelRatio: getChartPixelRatio(),
          responsive: true,
          maintainAspectRatio: true,
          animation: {
            duration: 1100,
            easing: "easeOutQuart",
          },
          plugins: {
            legend: {
              display: true,
              labels: {
                font: { family: "'Crimson Text', serif", size: 12 },
                color: colors.text,
              },
            },
            tooltip: {
              backgroundColor: colors.tooltipBg,
              titleColor: colors.tooltipTitle,
              bodyColor: colors.text,
              borderColor: colors.accent,
              borderWidth: 1,
              padding: 12,
              displayColors: false,
              titleFont: { family: "'Spectral', serif", size: 11 },
              bodyFont: { family: "'Crimson Text', serif", size: 12 },
            },
          },
          scales: {
            y: {
              ticks: {
                font: { family: "'Crimson Text',serif", size: 11 },
                color: colors.soft,
                callback: (value) =>
                  fmt(value, displayCurrency, { minimumFractionDigits: 0 }),
              },
              grid: {
                color: colors.grid,
                drawBorder: false,
              },
            },
            x: {
              ticks: {
                font: { family: "'Crimson Text',serif", size: 11 },
                color: colors.soft,
              },
              grid: {
                display: false,
                drawBorder: false,
              },
            },
          },
        }}
        />
      </div>
    </div>
  );
};

export default GraficoLucro;
